import { useMemo, type CSSProperties } from 'react';
import type { BoundingBox } from '@eye-able/types';
import {
  DIALOG_SIZES,
  PREVIEW_SIZES,
  PreviewDialog,
  PreviewStateFrame,
  type PreviewSize,
} from '@components/imagePreview';
import LoadingSpinner from '@components/LoadingSpinner';
import useLoadImage from '@/hooks/useLoadImage';
import useDisclosure from '@/hooks/useDisclosure';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { T } from '@/i18n/T';
import NoPreviewFallback from './NoPreviewFallback';
import BoundingBoxOverlay from './BoundingBoxOverlay';
import {
  buildCropUrl,
  clampCropToImage,
  getRoundedCrops,
  validateBoundingBoxValues,
} from './utils';
import { toCropRelativeRect } from './geometry';
import type { BoundingBoxPreviewProps, RoundedCrop } from './types';

const imageClassName = 'eye:block eye:h-full eye:w-full eye:object-fill';

// Reserve viewport space for the dialog chrome (title, padding, margins) so the
// enlarged crop fits without extra scrolling.
const DIALOG_IMAGE_VIEWPORT_OFFSET = 200;

/**
 * A cropped, zoomed-in preview of a single bounding box on a screenshot, with
 * an outline drawn around the box. Clicking opens an enlarged crop in a dialog.
 */
function BoundingBoxPreview({
  screenshotUrl = null,
  boundingBox,
  hideFallback = false,
  alt,
  size = 'inline',
  dialogSize = DIALOG_SIZES.default,
  interactive = true,
}: BoundingBoxPreviewProps) {
  const { t } = useInlineTranslation();
  const { isOpen, open, close } = useDisclosure(false);
  const label = alt ?? t('screenshot');

  const hasBoundingBox = validateBoundingBoxValues(boundingBox);

  const fullSizeImg = useLoadImage(
    screenshotUrl ?? undefined,
    undefined,
    hasBoundingBox,
  );

  const crops = useBoundingBoxCrops(boundingBox, fullSizeImg.image);
  const previewSize = PREVIEW_SIZES[size];
  const previewImg = usePreviewImage(
    screenshotUrl,
    hasBoundingBox ? boundingBox : null,
    crops?.previewCrop,
    Math.min(
      crops?.previewCrop.cropWidth ?? previewSize.imageWidth,
      previewSize.imageWidth,
    ),
  );

  if (
    !hasBoundingBox ||
    !screenshotUrl ||
    fullSizeImg.isError ||
    previewImg.isError
  ) {
    if (hideFallback) return null;

    return (
      <PreviewStateFrame size={size} bordered={false}>
        <NoPreviewFallback />
      </PreviewStateFrame>
    );
  }

  if (!crops || !fullSizeImg.isLoaded || !previewImg.isLoaded) {
    return (
      <PreviewStateFrame size={size}>
        <LoadingSpinner />
      </PreviewStateFrame>
    );
  }

  const previewRect = toCropRelativeRect(boundingBox, crops.previewCrop);
  const previewLayout = getContainedCropLayout(crops.previewCrop, previewSize);

  return (
    <PreviewDialog
      ariaLabel={label}
      title={alt ?? <T k='screenshot' />}
      interactive={interactive}
      isOpen={isOpen}
      onOpenChange={(next) => (next ? open() : close())}
      previewSize={size}
      size={dialogSize}
      preview={
        <div className='eye:absolute eye:overflow-hidden' style={previewLayout}>
          <img
            alt={label}
            loading='eager'
            src={previewImg.image?.src}
            className={imageClassName}
          />
          <BoundingBoxOverlay rects={[previewRect]} variant='outline' />
        </div>
      }
    >
      <EnlargedBoundingBox
        alt={label}
        screenshotUrl={screenshotUrl}
        boundingBox={boundingBox}
        crop={crops.enlargedCrop}
        isDialogOpen={isOpen}
      />
    </PreviewDialog>
  );
}

function EnlargedBoundingBox({
  alt,
  screenshotUrl,
  boundingBox,
  crop,
  isDialogOpen,
}: {
  alt: string;
  screenshotUrl: string | null;
  boundingBox: BoundingBox;
  crop: RoundedCrop;
  isDialogOpen: boolean;
}) {
  const image = useEnlargedImage(
    screenshotUrl,
    boundingBox,
    crop,
    isDialogOpen,
  );

  const cropAspectRatio = crop.cropWidth / crop.cropHeight;
  const viewportBudget = `100dvh - ${DIALOG_IMAGE_VIEWPORT_OFFSET}px`;

  const wrapperStyles: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    maxWidth: `calc((${viewportBudget}) * ${cropAspectRatio})`,
    maxHeight: `calc(${viewportBudget})`,
    aspectRatio: `${crop.cropWidth}/${crop.cropHeight}`,
    margin: '0 auto',
  };

  if (image.isError) return <NoPreviewFallback />;
  if (!image.isLoaded)
    return <LoadingSpinner height={PREVIEW_SIZES.inline.height} />;

  const enlargedRect = toCropRelativeRect(boundingBox, crop);

  return (
    <div style={wrapperStyles}>
      <img alt={alt} src={image.image?.src} className={imageClassName} />
      <BoundingBoxOverlay rects={[enlargedRect]} variant='outline' />
    </div>
  );
}

function useBoundingBoxCrops(
  boundingBox: BoundingBox | null | undefined,
  fullSizeImg: HTMLImageElement | null | undefined,
) {
  return useMemo(() => {
    if (!boundingBox || !fullSizeImg) return null;

    const { previewCrop, enlargedCrop } = getRoundedCrops(boundingBox);

    return {
      previewCrop: clampCropToImage(previewCrop, fullSizeImg),
      enlargedCrop: clampCropToImage(enlargedCrop, fullSizeImg),
    };
  }, [boundingBox, fullSizeImg]);
}

/**
 * Fits a crop into a fixed preview frame without changing its aspect ratio.
 * The bounding-box overlay is rendered in the same area, so it stays aligned
 * when a wide or tall crop leaves horizontal or vertical bars.
 */
function getContainedCropLayout(
  crop: RoundedCrop,
  frame: Pick<(typeof PREVIEW_SIZES)[PreviewSize], 'width' | 'height'>,
): CSSProperties {
  // Do not scale a small source crop up: that is visibly blurry, especially
  // for narrow text snippets. Larger crops scale down to fit the frame.
  const scale = Math.min(
    frame.width / crop.cropWidth,
    frame.height / crop.cropHeight,
    1,
  );
  const width = crop.cropWidth * scale;
  const height = crop.cropHeight * scale;

  return {
    width,
    height,
    left: (frame.width - width) / 2,
    top: (frame.height - height) / 2,
  };
}

function usePreviewImage(
  screenshotUrl: string | null,
  boundingBox: BoundingBox | null | undefined,
  crop: RoundedCrop | null | undefined,
  targetWidth: number,
) {
  const previewImageSrc = useMemo(() => {
    if (!screenshotUrl || !boundingBox || !crop) return null;

    return buildCropUrl({
      baseUrl: screenshotUrl,
      ...crop,
      targetWidth,
    });
  }, [boundingBox, crop, screenshotUrl, targetWidth]);

  return useLoadImage(previewImageSrc ?? undefined);
}

function useEnlargedImage(
  screenshotUrl: string | null,
  boundingBox: BoundingBox | null | undefined,
  crop: RoundedCrop | null | undefined,
  isDialogOpen: boolean,
) {
  const enlargedImageSrc = useMemo(() => {
    if (!screenshotUrl || !boundingBox || !crop) return null;

    return buildCropUrl({
      baseUrl: screenshotUrl,
      ...crop,
      targetWidth: crop.cropWidth,
    });
  }, [boundingBox, crop, screenshotUrl]);

  return useLoadImage(enlargedImageSrc ?? undefined, undefined, isDialogOpen);
}

export default BoundingBoxPreview;
